// Path: src/pages/TradingData/CandleUpload.jsx
// Upload TradingView CSV exports into candle tables
// Features: 5000/batch, 3 parallel, ETH/RTH session breakdown + selector

import React, { useState, useRef } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Chip,
  alpha, Snackbar, Alert, LinearProgress,
  Select, MenuItem, FormControl,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  InsertDriveFile as FileIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';

import { MONO, ASSETS, TIMEFRAMES, TIMEFRAME_LABELS } from './IndicatorConfigs';

const SESSIONS = ['ALL', 'ETH', 'RTH'];
const BATCH_SIZE = 5000;
const PARALLEL = 3;

const Pill = ({ active, activeColor, children, onClick }) => (
  <Button onClick={onClick} variant="outlined" size="small" sx={{
    minWidth: 0, py: 0.6, px: 1.5, fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO,
    borderRadius: 1.5, textTransform: 'none',
    borderColor: active ? activeColor : alpha('#fff', 0.08),
    bgcolor: active ? alpha(activeColor, 0.12) : 'transparent',
    color: active ? activeColor : alpha('#fff', 0.3),
    '&:hover': { borderColor: active ? activeColor : alpha('#fff', 0.2), bgcolor: active ? alpha(activeColor, 0.18) : alpha('#fff', 0.04) },
  }}>
    {children}
  </Button>
);

function classifySession(timeStr) {
  try {
    const dt = new Date(timeStr);
    const h = dt.getHours(), m = dt.getMinutes();
    const total = h * 60 + m;
    return (total >= 570 && total < 960) ? 'RTH' : 'ETH'; // 9:30-16:00
  } catch { return 'ETH'; }
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(/[,\t]/).map(h => h.trim().toLowerCase());
  const timeIdx = headers.findIndex(h => ['time', 'date', 'datetime', 'timestamp'].includes(h));
  const openIdx = headers.indexOf('open');
  const highIdx = headers.indexOf('high');
  const lowIdx = headers.indexOf('low');
  const closeIdx = headers.indexOf('close');
  const volIdx = headers.findIndex(h => ['volume', 'vol'].includes(h));

  if (timeIdx === -1 || openIdx === -1 || highIdx === -1 || lowIdx === -1 || closeIdx === -1) {
    throw new Error(`Missing OHLC columns. Found: ${headers.join(', ')}`);
  }

  const candles = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(/[,\t]/);
    if (cols.length < 4) continue;

    const time = cols[timeIdx]?.trim();
    const open = parseFloat(cols[openIdx]);
    const high = parseFloat(cols[highIdx]);
    const low = parseFloat(cols[lowIdx]);
    const close = parseFloat(cols[closeIdx]);
    const volume = volIdx >= 0 ? parseInt(cols[volIdx]) || 0 : 0;

    if (!time || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) continue;

    candles.push({ time, open, high, low, close, volume, session: classifySession(time) });
  }

  return candles;
}

function countSessions(candles) {
  let eth = 0, rth = 0;
  for (const c of candles) {
    if (c.session === 'RTH') rth++;
    else eth++;
  }
  return { eth, rth };
}

async function sendBatch(url, headers, batch, asset, timeframe) {
  const payload = {
    candles: batch.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume, asset, timeframe })),
  };
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json();
    if (!res.ok) {
      return {
        inserted: 0,
        error_count: batch.length,
        error_message: data.error || data.detail || `HTTP ${res.status}`,
      };
    }
    return data;
  }
  // Non-JSON response — grab the first part of the body so we know why
  let bodyText = '';
  try { bodyText = (await res.text()).slice(0, 200).replace(/\s+/g, ' ').trim(); } catch {}
  return {
    inserted: 0,
    error_count: batch.length,
    error_message: `HTTP ${res.status} ${res.statusText} · ${bodyText || '(empty body)'}`,
  };
}

export default function CandleUpload() {
  const [asset, setAsset] = useState('NQ');
  const [timeframe, setTimeframe] = useState('1m');
  const [session, setSession] = useState('ALL');
  const [file, setFile] = useState(null);
  const [allCandles, setAllCandles] = useState(null);   // full parsed set
  const [parsedSessions, setParsedSessions] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ sent: 0, total: 0, batches: 0, errors: 0 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef(null);

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const WEBHOOK_URL = `${API_BASE}/strategies/api/webhook/candles-naive/`;

  // Filter candles by selected session
  const filteredCandles = allCandles
    ? session === 'ALL'
      ? allCandles
      : allCandles.filter(c => c.session === session)
    : null;

  const handleFile = async (f) => {
    if (!f || (!f.name.endsWith('.csv') && f.type !== 'text/csv' && f.type !== 'application/vnd.ms-excel')) {
      setSnackbar({ open: true, message: 'Please select a CSV file', severity: 'error' });
      return;
    }

    setFile(f);
    setResult(null);
    setError(null);
    setParseError(null);
    setAllCandles(null);
    setParsedSessions(null);

    try {
      const text = await f.text();
      const candles = parseCSV(text);
      if (candles.length === 0) {
        setParseError('No valid candles found in CSV');
        return;
      }
      setAllCandles(candles);
      setParsedSessions(countSessions(candles));
    } catch (err) {
      setParseError(err.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!filteredCandles?.length) return;
    setUploading(true);
    setError(null);
    setResult(null);

    const startTime = Date.now();
    const total = filteredCandles.length;
    const numBatches = Math.ceil(total / BATCH_SIZE);
    let totalInserted = 0;
    let totalErrors = 0;

    setProgress({ sent: 0, total, batches: numBatches, errors: 0 });

    const token = localStorage.getItem('access_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

    const batches = [];
    for (let i = 0; i < numBatches; i++) {
      batches.push(filteredCandles.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE));
    }

    const distinctErrors = new Set();
    let completed = 0;
    for (let i = 0; i < batches.length; i += PARALLEL) {
      const group = batches.slice(i, i + PARALLEL);

      const results = await Promise.allSettled(
        group.map(batch => sendBatch(WEBHOOK_URL, headers, batch, asset, timeframe))
      );

      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          totalInserted += r.value.inserted || 0;
          totalErrors += r.value.error_count || 0;
          if (r.value.error_message) distinctErrors.add(r.value.error_message);
          if (r.value.errors && Array.isArray(r.value.errors)) {
            r.value.errors.slice(0, 3).forEach(e => distinctErrors.add(e));
          }
        } else {
          totalErrors += BATCH_SIZE;
          if (r.reason) distinctErrors.add(String(r.reason).slice(0, 200));
        }
      }

      completed += group.length;
      const sent = Math.min(completed * BATCH_SIZE, total);
      setProgress({ sent, total, batches: numBatches, errors: totalErrors });
      setElapsed(Math.round((Date.now() - startTime) / 1000));
    }

    const totalTime = Math.round((Date.now() - startTime) / 1000);
    const firstTime = filteredCandles[0]?.time?.replace('T', ' ').slice(0, 19) || '';
    const lastTime = filteredCandles[filteredCandles.length - 1]?.time?.replace('T', ' ').slice(0, 19) || '';
    const sessions = countSessions(filteredCandles);

    const resultData = {
      inserted: totalInserted,
      errors: totalErrors,
      total: filteredCandles.length,
      asset,
      timeframe,
      session,
      table: `candles_${timeframe}`,
      first_candle: firstTime,
      last_candle: lastTime,
      batches: numBatches,
      seconds: totalTime,
      eth_count: sessions.eth,
      rth_count: sessions.rth,
    };

    if (totalInserted > 0) {
      setResult(resultData);
      setHistory(prev => [{ ...resultData, filename: file.name, uploadedAt: new Date().toLocaleTimeString() }, ...prev]);
      setSnackbar({ open: true, message: `${totalInserted.toLocaleString()} candles imported in ${totalTime}s`, severity: 'success' });
      setFile(null);
      setAllCandles(null);
      setParsedSessions(null);
    } else {
      const sample = [...distinctErrors].slice(0, 3).join(' | ');
      setError(
        `Upload failed — 0 candles inserted, ${totalErrors.toLocaleString()} errors.` +
        (sample ? `\nServer said: ${sample}` : '\n(No error detail from server)')
      );
    }

    setUploading(false);
  };

  const ac = '#a855f7';
  const pct = progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;
  const filteredCount = filteredCandles?.length || 0;
  const filteredBatches = Math.ceil(filteredCount / BATCH_SIZE);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto', marginTop: '50px' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <StorageIcon sx={{ color: ac, fontSize: 28 }} />
        <Box>
          <Typography sx={{ color: '#e2e8f0', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
            Candle Data Upload
          </Typography>
          <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.65rem', fontFamily: MONO, letterSpacing: '0.1em' }}>
            IMPORT TRADINGVIEW CSV → CANDLE TABLES ({BATCH_SIZE.toLocaleString()}/batch × {PARALLEL} parallel)
          </Typography>
        </Box>
      </Stack>

      <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha(ac, 0.1)}`, borderRadius: 2.5, p: 3 }}>
        <Stack spacing={2.5}>
          {/* Asset + Timeframe + Session */}
          <Stack direction="row" spacing={2}>
            <Box>
              <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.2em', fontFamily: MONO, mb: 0.5 }}>ASSET</Typography>
              <Stack direction="row" spacing={0.4}>
                {ASSETS.map(a => <Pill key={a} active={asset === a} activeColor="#22c55e" onClick={() => setAsset(a)}>{a}</Pill>)}
              </Stack>
            </Box>
            <Box>
              <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.2em', fontFamily: MONO, mb: 0.5 }}>TIMEFRAME</Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  sx={{
                    fontFamily: MONO, fontSize: '0.78rem', fontWeight: 700,
                    color: ac, borderRadius: 1.5,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(ac, 0.25) },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(ac, 0.5) },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ac },
                    '& .MuiSelect-icon': { color: alpha(ac, 0.6) },
                    bgcolor: alpha(ac, 0.06),
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#0d1117', border: `1px solid ${alpha(ac, 0.2)}`,
                        borderRadius: 2, mt: 0.5,
                      },
                    },
                  }}
                >
                  {TIMEFRAMES.map(t => (
                    <MenuItem key={t} value={t} sx={{
                      fontFamily: MONO, fontSize: '0.78rem', fontWeight: 600,
                      color: alpha('#fff', 0.7),
                      '&.Mui-selected': { color: ac, bgcolor: alpha(ac, 0.1) },
                      '&:hover': { bgcolor: alpha(ac, 0.07) },
                    }}>
                      {TIMEFRAME_LABELS[t] || t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.2em', fontFamily: MONO, mb: 0.5 }}>SESSION</Typography>
              <Stack direction="row" spacing={0.4}>
                {SESSIONS.map(s => <Pill key={s} active={session === s} activeColor="#f59e0b" onClick={() => setSession(s)}>{s}</Pill>)}
              </Stack>
            </Box>
          </Stack>

          {/* Drop zone */}
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragging ? ac : alpha('#fff', 0.1)}`,
              borderRadius: 2.5, bgcolor: dragging ? alpha(ac, 0.06) : alpha('#000', 0.3),
              py: 4, px: 3, textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
              transition: 'all 0.2s',
              '&:hover': uploading ? {} : { borderColor: alpha(ac, 0.4), bgcolor: alpha(ac, 0.04) },
            }}
          >
            <input ref={inputRef} type="file" accept=".csv" hidden onChange={(e) => handleFile(e.target.files?.[0])} />

            {file ? (
              <Stack alignItems="center" spacing={1}>
                <FileIcon sx={{ fontSize: 36, color: allCandles ? '#22c55e' : '#f59e0b' }} />
                <Typography sx={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: 700, fontFamily: MONO }}>{file.name}</Typography>
                <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.65rem', fontFamily: MONO }}>
                  {(file.size / 1024).toFixed(0)} KB · {asset} · {timeframe} · {session}
                </Typography>
                {allCandles && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                    {session !== 'ALL' && allCandles.length !== filteredCount && (
                      <Chip label={`${allCandles.length.toLocaleString()} total`} size="small"
                        sx={{ height: 20, fontSize: '0.55rem', fontWeight: 600, bgcolor: alpha('#fff', 0.05), color: alpha('#fff', 0.3), '& .MuiChip-label': { px: 0.6 } }} />
                    )}
                    <Chip label={`${filteredCount.toLocaleString()} ${session === 'ALL' ? 'candles' : session + ' candles'}`} size="small"
                      sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha('#22c55e', 0.12), color: '#22c55e', '& .MuiChip-label': { px: 0.6 } }} />
                    <Chip label={`${filteredBatches} batches`} size="small"
                      sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(ac, 0.12), color: ac, '& .MuiChip-label': { px: 0.6 } }} />
                    {parsedSessions && (
                      <>
                        <Chip label={`ETH ${parsedSessions.eth.toLocaleString()}`} size="small"
                          sx={{ height: 20, fontSize: '0.55rem', fontWeight: 700,
                            bgcolor: alpha('#f59e0b', session === 'ETH' ? 0.2 : 0.08),
                            color: session === 'ETH' ? '#f59e0b' : alpha('#f59e0b', 0.5),
                            border: session === 'ETH' ? `1px solid ${alpha('#f59e0b', 0.4)}` : 'none',
                            '& .MuiChip-label': { px: 0.6 } }} />
                        <Chip label={`RTH ${parsedSessions.rth.toLocaleString()}`} size="small"
                          sx={{ height: 20, fontSize: '0.55rem', fontWeight: 700,
                            bgcolor: alpha('#00d4ff', session === 'RTH' ? 0.2 : 0.08),
                            color: session === 'RTH' ? '#00d4ff' : alpha('#00d4ff', 0.5),
                            border: session === 'RTH' ? `1px solid ${alpha('#00d4ff', 0.4)}` : 'none',
                            '& .MuiChip-label': { px: 0.6 } }} />
                      </>
                    )}
                    <Chip label={`${filteredCandles?.[0]?.time?.slice(0, 10)} → ${filteredCandles?.[filteredCount - 1]?.time?.slice(0, 10)}`} size="small"
                      sx={{ height: 20, fontSize: '0.55rem', fontWeight: 600, bgcolor: alpha('#fff', 0.05), color: alpha('#fff', 0.4), '& .MuiChip-label': { px: 0.6 } }} />
                  </Stack>
                )}
                {!uploading && (
                  <Button size="small" onClick={(e) => { e.stopPropagation(); setFile(null); setAllCandles(null); setParsedSessions(null); }}
                    sx={{ color: alpha('#fff', 0.3), fontSize: '0.6rem', fontFamily: MONO, textTransform: 'none' }}>
                    Remove
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={1}>
                <UploadIcon sx={{ fontSize: 36, color: alpha('#fff', 0.15) }} />
                <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.82rem', fontWeight: 600, fontFamily: MONO }}>
                  Drop TradingView CSV here
                </Typography>
                <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.6rem', fontFamily: MONO }}>
                  or click to browse · time, open, high, low, close
                </Typography>
              </Stack>
            )}
          </Box>

          {parseError && (
            <Paper elevation={0} sx={{ bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.3)}`, borderRadius: 1.5, p: 1.5 }}>
              <Typography sx={{ color: '#ef4444', fontSize: '0.72rem', fontWeight: 600, fontFamily: MONO }}>Parse error: {parseError}</Typography>
            </Paper>
          )}

          {uploading && (
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ color: ac, fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO }}>
                  Batch {Math.min(Math.ceil(progress.sent / BATCH_SIZE), progress.batches)}/{progress.batches} · {elapsed}s
                </Typography>
                <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.62rem', fontFamily: MONO }}>
                  {progress.sent.toLocaleString()}/{progress.total.toLocaleString()} · {pct}%
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct}
                sx={{ height: 6, borderRadius: 3, bgcolor: alpha(ac, 0.1), '& .MuiLinearProgress-bar': { bgcolor: ac, borderRadius: 3 } }} />
            </Box>
          )}

          <Button
            fullWidth variant="contained" disabled={!filteredCount || uploading}
            onClick={handleUpload}
            startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
            sx={{
              bgcolor: ac, color: '#fff', fontWeight: 800, fontSize: '0.8rem', fontFamily: MONO,
              letterSpacing: '0.1em', borderRadius: 1.5, py: 1.2, textTransform: 'none',
              boxShadow: `0 4px 20px ${alpha(ac, 0.35)}`,
              '&:hover': { bgcolor: '#9333ea' },
              '&.Mui-disabled': { bgcolor: alpha(ac, 0.2), color: alpha('#fff', 0.4) },
            }}
          >
            {uploading
              ? `UPLOADING... ${pct}%`
              : filteredCount
                ? `UPLOAD ${filteredCount.toLocaleString()} ${session !== 'ALL' ? session + ' ' : ''}CANDLES → candles_${timeframe}`
                : `UPLOAD → candles_${timeframe}`}
          </Button>

          {error && (
            <Paper elevation={0} sx={{ bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.3)}`, borderRadius: 1.5, p: 1.5 }}>
              <Typography sx={{
                color: '#ef4444', fontSize: '0.72rem', fontWeight: 600, fontFamily: MONO,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {error}
              </Typography>
            </Paper>
          )}

          {/* Result */}
          {result && (
            <Paper elevation={0} sx={{ bgcolor: alpha('#22c55e', 0.04), border: `1px solid ${alpha('#22c55e', 0.15)}`, borderRadius: 2, p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <CheckIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                <Typography sx={{ color: '#22c55e', fontSize: '0.82rem', fontWeight: 800, fontFamily: MONO }}>
                  {result.inserted.toLocaleString()} candles imported in {result.seconds}s ({result.batches} batches)
                </Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
                {[
                  { label: 'TABLE', value: result.table, color: ac },
                  { label: 'ASSET', value: result.asset, color: '#22c55e' },
                  { label: 'TIMEFRAME', value: result.timeframe, color: '#00d4ff' },
                  { label: 'SESSION', value: result.session, color: '#f59e0b' },
                  { label: 'FIRST CANDLE', value: result.first_candle, color: alpha('#fff', 0.5) },
                  { label: 'LAST CANDLE', value: result.last_candle, color: alpha('#fff', 0.5) },
                  { label: 'ETH', value: result.eth_count?.toLocaleString(), color: '#f59e0b' },
                  { label: 'RTH', value: result.rth_count?.toLocaleString(), color: '#00d4ff' },
                ].map(s => (
                  <Box key={s.label}>
                    <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.45rem', fontWeight: 700, letterSpacing: '0.15em', fontFamily: MONO }}>{s.label}</Typography>
                    <Typography sx={{ color: s.color, fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO }}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>
              {result.errors > 0 && (
                <Typography sx={{ color: '#ef4444', fontSize: '0.6rem', fontFamily: MONO, mt: 1 }}>
                  ⚠ {result.errors} errors
                </Typography>
              )}
            </Paper>
          )}

          {/* Upload history */}
          {history.length > 0 && (
            <Box>
              <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.15em', fontFamily: MONO, mb: 0.8 }}>
                UPLOAD HISTORY
              </Typography>
              <Stack spacing={0.5}>
                {history.map((h, i) => (
                  <Paper key={i} elevation={0} sx={{ bgcolor: alpha('#fff', 0.02), border: `1px solid ${alpha('#fff', 0.04)}`, borderRadius: 1, px: 1.5, py: 0.6 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                        <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem', fontFamily: MONO }}>{h.filename}</Typography>
                        <Chip label={`${h.asset} · ${h.timeframe} · ${h.session}`} size="small" sx={{ height: 16, fontSize: '0.48rem', fontWeight: 700, bgcolor: alpha(ac, 0.1), color: ac, '& .MuiChip-label': { px: 0.5 } }} />
                        <Chip label={`ETH ${h.eth_count?.toLocaleString()}`} size="small" sx={{ height: 16, fontSize: '0.45rem', fontWeight: 600, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', '& .MuiChip-label': { px: 0.4 } }} />
                        <Chip label={`RTH ${h.rth_count?.toLocaleString()}`} size="small" sx={{ height: 16, fontSize: '0.45rem', fontWeight: 600, bgcolor: alpha('#00d4ff', 0.1), color: '#00d4ff', '& .MuiChip-label': { px: 0.4 } }} />
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ color: '#22c55e', fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO }}>{h.inserted?.toLocaleString()} rows · {h.seconds}s</Typography>
                        <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.5rem', fontFamily: MONO }}>{h.uploadedAt}</Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled"
          sx={{ fontWeight: 700, fontFamily: MONO, fontSize: '0.78rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
